/**
 * usePasskeys
 *
 * React hook binding the passkey service (@packages/supabase/passkey.service)
 * to the app's Supabase context, with React state for UX (loading, list,
 * last-error).
 *
 * Feature folder: documentation/features/passkeys/
 * Plan doc:       documentation/features/passkeys/plan.md
 *
 * Usage:
 *   const { passkeys, registerPasskey, signInWithPasskey, remove, loading, error, isSupported } = usePasskeys()
 *
 * Stays intentionally thin — all business logic is in passkey.service.ts so
 * it can be swapped for supabase-js native calls once PR #2263 ships.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSupabase } from '../context'
import {
  createPasskeyService,
  PasskeyServiceError,
  PasskeySummary,
  RegisterPasskeyOptions,
  SignInWithPasskeyOptions,
  isPasskeySupported
} from '../passkey.service'

export interface UsePasskeysApi {
  readonly loading: boolean
  readonly error: PasskeyServiceError | null
  readonly passkeys: PasskeySummary[]
  readonly isSupported: boolean
  readonly refresh: () => Promise<void>
  readonly registerPasskey: (opts?: RegisterPasskeyOptions) => Promise<void>
  readonly signInWithPasskey: (opts?: SignInWithPasskeyOptions) => Promise<void>
  readonly stepUpWithPasskey: () => Promise<boolean>
  readonly rename: (id: string, deviceName: string) => Promise<void>
  readonly remove: (id: string) => Promise<void>
  readonly hasFreshStepUp: (windowMs?: number) => Promise<boolean>
}

export function usePasskeys(): UsePasskeysApi {
  const { supabase, session } = useSupabase()
  const service = useMemo(() => createPasskeyService(supabase), [supabase])

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<PasskeyServiceError | null>(null)
  const [passkeys, setPasskeys] = useState<PasskeySummary[]>([])

  const isSupported = useMemo(() => isPasskeySupported(), [])

  const refresh = useCallback(async () => {
    if (!session) {
      setPasskeys([])
      return
    }
    setLoading(true)
    setError(null)
    try {
      const list = await service.passkey.list()
      setPasskeys(list)
    } catch (e) {
      setError(e as PasskeyServiceError)
    } finally {
      setLoading(false)
    }
  }, [service, session])

  useEffect(() => {
    // Only fetch when the session exists — otherwise RLS will return []
    // and we'd thrash on mount while the session is still loading.
    if (session) void refresh()
  }, [session, refresh])

  const wrap = useCallback(
    <A extends unknown[], T>(fn: (...args: A) => Promise<T>) =>
      async (...args: A): Promise<T> => {
        setLoading(true)
        setError(null)
        try {
          return await fn(...args)
        } catch (e) {
          setError(e as PasskeyServiceError)
          throw e
        } finally {
          setLoading(false)
        }
      },
    []
  )

  const registerPasskey = useCallback(
    wrap(async (opts?: RegisterPasskeyOptions) => {
      await service.registerPasskey(opts ?? {})
      await refresh()
    }),
    [service, refresh, wrap]
  )

  const signInWithPasskey = useCallback(
    wrap(async (opts?: SignInWithPasskeyOptions) => {
      await service.signInWithPasskey(opts ?? {})
    }),
    [service, wrap]
  )

  const stepUpWithPasskey = useCallback(
    wrap(async () => {
      await service.stepUpWithPasskey()
      return true
    }),
    [service, wrap]
  )

  const rename = useCallback(
    wrap(async (id: string, deviceName: string) => {
      await service.passkey.update(id, deviceName)
      await refresh()
    }),
    [service, refresh, wrap]
  )

  const remove = useCallback(
    wrap(async (id: string) => {
      await service.passkey.delete(id)
      await refresh()
    }),
    [service, refresh, wrap]
  )

  const hasFreshStepUp = useCallback(
    (windowMs?: number) => service.hasFreshStepUp(windowMs),
    [service]
  )

  return {
    loading,
    error,
    passkeys,
    isSupported,
    refresh,
    registerPasskey,
    signInWithPasskey,
    stepUpWithPasskey,
    rename,
    remove,
    hasFreshStepUp
  }
}
